// React imports
import { useState } from 'react';

// Types
import { Invoice, SavePromocode } from '@spike/promocode-model';

// Hooks
import { usePromoCodes } from '../PromoCodesProvider';

// UI Components
import { OverFullWindow } from 'components/UI';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import SuccessScreen from 'components/UI/V3/SuccessScreen';

// Local Components
import { AddPromoCode } from '../Add/AddPromoCode';
import CustomButton from './CustomButton';
import CustomHeaderTitle from './CustomHeaderTitle';
import PromoCodeDetailModal from './PromoCodeDetailModal';
import PromoCodeUsageTable from './PromoCodeUsageTable';
import SummaryCards from './SummaryCards';

// Utils
import { formatPromoCodeName, convertPromocodeToSavePromocode } from '../Utils';

// Styles
import styles from './PromoCodesUsage.module.scss';
import OrderDetail from 'components/OrderDetail';
import InvoiceContainer from './InvoiceContainer';
import Checkout from 'components/Checkout2';

interface PromoCodesUsageProps {
    onBack?: () => void;
}

// const bookings = [] as any; // Removed - using redeems from provider instead

const PromoCodesUsage = ({ onBack /* promoCode */ /*  onPromoCodeSuccess */ }: PromoCodesUsageProps) => {
    const {
        editPromoCode,

        redeems,
        setRedeemsSearchText,
        redeemsSearchText,

        showUsage,
        isLoading,

        openEditForm,
        closeForm,
        setShowUsage,

        clearSuccess
    } = usePromoCodes();
    // const [searchText, setSearchText] = useState('');
    const [showDetails, setShowDetails] = useState(false);
    const [showOrderDetails, setShowOrderDetails] = useState<{
        bookingId: number | null;
        orderId: number | null;
    } | null>(null);
    const [checkoutData, setCheckoutData] = useState<
        { orderId?: number; bookingId: number; back: boolean; hide: boolean } | undefined
    >();
    const [showInvoice, setShowInvoice] = useState<Invoice | null>(null);
    const [success, setSuccess] = useState<SavePromocode>({} as SavePromocode);

    const formattedName = formatPromoCodeName(showUsage!);

    const handleShowDetails = () => {
        setShowDetails(true);
    };

    const handleEditPromo = () => {
        clearSuccess(); // Clear any previous SaveSuccess status
        setShowDetails(false);
        openEditForm(showUsage!);
    };

    const handleCloseEdit = () => {
        closeForm();
    };

    const handlePromoCodeSuccess = (savedPromoCode: SavePromocode) => {
        setSuccess(savedPromoCode);
    };
    const handleUsageSuccessClose = () => {
        setSuccess({} as SavePromocode);
        closeForm();
        setShowUsage(null);
    };

    const handleViewBooking = (bookingId: number, orderId: number) => {
        console.log(bookingId, orderId);
        setShowOrderDetails({ bookingId, orderId });
    };

    const handleViewInvoice = (invoice: Invoice) => {
        setShowInvoice(invoice);
    };

    const checkoutHandler = (bookingId: number, orderId?: number, enableBack?: boolean) => {
        setShowOrderDetails(null);
        setCheckoutData({ orderId, bookingId, back: enableBack ?? false, hide: false });
    };

    const checkoutCloseHandler = () => {
        setCheckoutData(undefined);
    };

    const checkoutBackHandler = () => {
        if (checkoutData) {
            setShowOrderDetails({ bookingId: checkoutData.bookingId, orderId: checkoutData.orderId! });
        }
        setCheckoutData(undefined);
    };

    const handleShowClient = () => {
        setCheckoutData(prev => (prev === undefined ? undefined : { ...prev, hide: true }));
    };

    const handlePayByLinkSent = () => {
        setCheckoutData(undefined);
    };

    if (success.name) {
        return (
            <OverFullWindow
                showMobileLogo
                title="Editing Promo Code"
                onClose={handleUsageSuccessClose}
            >
                <SuccessScreen
                    onClick={handleUsageSuccessClose}
                    title={'Good Job'}
                    message={
                        <SuccessMessage
                            promoName={success.name}
                            isEditing={true}
                        />
                    }
                    buttonLabel={'Back to Promo Codes'}
                />
            </OverFullWindow>
        );
    }

    return (
        <div className={styles.wrapperContainer}>
            {editPromoCode?.id ? (
                <AddPromoCode
                    onClose={handleCloseEdit}
                    onSuccess={handlePromoCodeSuccess}
                    promoCode={convertPromocodeToSavePromocode(showUsage!)}
                    isEditing={true}
                />
            ) : showInvoice ? (
                <InvoiceContainer
                    pdfUrl={showInvoice.pdfUrl}
                    onBackInvoice={() => setShowInvoice(null)}
                    invoiceNumber={showInvoice.number}
                />
            ) : (
                <>
                    <div className={styles.headWrapper}>
                        <DefaultHeader
                            title={
                                <CustomHeaderTitle
                                    title={formattedName.name}
                                    subtitle={formattedName.value}
                                    promoCodeStatus={showUsage!.active}
                                />
                            }
                            onBack={onBack}
                            customButton={
                                <CustomButton
                                    title="Promo Details"
                                    onAction={handleShowDetails}
                                />
                            }
                            className={styles.flexStart}
                        />
                        <SummaryCards
                            sales={showUsage!.totalSales}
                            clients={showUsage!.totalRedeems}
                        />
                        <SearchBar
                            searchText={redeemsSearchText}
                            onChangeSearchText={setRedeemsSearchText}
                            placeholder="Search by client"
                            className={styles.searchBarContainer}
                        />
                        <PromoCodeUsageTable
                            redeems={redeems}
                            isEmpty={redeems.length === 0}
                            isLoading={isLoading}
                            onViewBooking={handleViewBooking}
                            onViewInvoice={handleViewInvoice}
                        />
                    </div>
                    <PromoCodeDetailModal
                        isOpen={showDetails}
                        onClose={() => setShowDetails(false)}
                        onEditPromo={handleEditPromo}
                    />
                    {showOrderDetails && (
                        <OrderDetail
                            bookingId={showOrderDetails.bookingId!}
                            orderId={showOrderDetails.orderId!}
                            onClose={() => setShowOrderDetails(null)}
                            onCheckout={(bookingId, orderId) => checkoutHandler(bookingId, orderId, true)}
                        />
                    )}
                    {checkoutData && (
                        <Checkout
                            orderId={checkoutData.orderId}
                            bookingId={checkoutData.bookingId}
                            onClose={checkoutCloseHandler}
                            onViewBooking={checkoutData.back ? checkoutBackHandler : undefined}
                            onBack={checkoutData.back ? checkoutBackHandler : undefined}
                            onEditClient={handleShowClient}
                            onPayByLinkSent={handlePayByLinkSent}
                            hide={checkoutData.hide}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default PromoCodesUsage;
const SuccessMessage = ({ promoName, isEditing }: { promoName: string; isEditing: boolean }) => {
    return (
        <div style={{ textAlign: 'center' }}>
            <span style={{ fontWeight: 'bold' }}>{promoName}</span>
            <span>{` was successfully ${isEditing ? 'edited.' : 'created.'}`}</span>
        </div>
    );
};
